import React, { useState, useEffect } from 'react';
import { Bell, Send, Trash2, Users, User, Info, AlertTriangle, ShieldCheck, Globe, Search, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [search, setSearch] = useState("");

  const [newNoti, setNewNoti] = useState({
    title: "",
    message: "",
    type: "info",
    is_global: true,
    user_id: null,
    targetUsername: ""
  });

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleSend = async () => {
    if (!newNoti.title || !newNoti.message) {
      toast.error("Message content incomplete");
      return;
    }

    setIsSending(true);
    try {
      let userId = null;
      if (!newNoti.is_global) {
        const { data, error } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', newNoti.targetUsername.replace('@', ''))
          .single();
        
        if (error || !data) {
          toast.error("Target architect not found");
          setIsSending(false);
          return;
        }
        userId = data.id;
      }

      const { error } = await supabase.from('notifications').insert([{
        title: newNoti.title,
        message: newNoti.message,
        type: newNoti.type,
        is_global: newNoti.is_global,
        user_id: userId
      }]);

      if (error) throw error;

      toast.success("Broadcast deployed successfully!");
      setNewNoti({ title: "", message: "", type: "info", is_global: true, user_id: null, targetUsername: "" });
      fetchNotifications();
    } catch (err) {
      toast.error("System dispatch error");
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await supabase.from('notifications').delete().eq('id', id);
      setNotifications(notifications.filter(n => n.id !== id));
      toast.info("Notification purged");
    } catch (err) {
      toast.error("Purge error");
    }
  };

  const filteredNotis = notifications.filter(n => 
    n.title.toLowerCase().includes(search.toLowerCase()) || 
    n.message.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">Broadcast Hub</h1>
        <p className="text-sonas-muted text-xs mt-1">System-wide alerts and direct architect comms.</p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-sonas-surface border border-sonas-border p-6 rounded-3xl space-y-6 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-widest text-sonas-dim flex items-center gap-2">
              <Send size={14} /> Dispatch Center
            </h3>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-sonas-dim uppercase">Scope</label>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => setNewNoti({...newNoti, is_global: true})}
                    className={cn("h-10 rounded-xl border text-[10px] font-bold uppercase transition-all", 
                      newNoti.is_global ? "bg-sonas-accent/10 border-sonas-accent/30 text-sonas-accent" : "border-white/5 bg-white/[0.02] text-sonas-dim")}
                  >
                    Global
                  </button>
                  <button 
                    onClick={() => setNewNoti({...newNoti, is_global: false})}
                    className={cn("h-10 rounded-xl border text-[10px] font-bold uppercase transition-all", 
                      !newNoti.is_global ? "bg-blue-500/10 border-blue-500/30 text-blue-500" : "border-white/5 bg-white/[0.02] text-sonas-dim")}
                  >
                    Targeted
                  </button>
                </div>
              </div>

              {!newNoti.is_global && (
                <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                  <label className="text-[10px] font-bold text-sonas-dim uppercase">Architect Identity</label>
                  <Input 
                    placeholder="@username" 
                    value={newNoti.targetUsername}
                    onChange={e => setNewNoti({...newNoti, targetUsername: e.target.value})}
                    className="bg-sonas-surface2 border-white/5 rounded-xl h-11"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-sonas-dim uppercase">Alert Level</label>
                <Select value={newNoti.type} onValueChange={val => setNewNoti({...newNoti, type: val})}>
                  <SelectTrigger className="bg-sonas-surface2 border-white/5 h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-sonas-bg border-white/10">
                    <SelectItem value="info">Information (Blue)</SelectItem>
                    <SelectItem value="success">Success (Green)</SelectItem>
                    <SelectItem value="warning">System Warning (Amber)</SelectItem>
                    <SelectItem value="alert">Critical Alert (Red)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-sonas-dim uppercase">Subject</label>
                <Input 
                  placeholder="e.g. Server Maintenance" 
                  value={newNoti.title}
                  onChange={e => setNewNoti({...newNoti, title: e.target.value})}
                  className="bg-sonas-surface2 border-white/5 rounded-xl h-11"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-sonas-dim uppercase">Message Content</label>
                <Textarea 
                  placeholder="Describe the alert..." 
                  value={newNoti.message}
                  onChange={e => setNewNoti({...newNoti, message: e.target.value})}
                  className="bg-sonas-surface2 border-white/5 rounded-xl min-h-[120px] text-xs"
                />
              </div>

              <Button 
                onClick={handleSend}
                disabled={isSending}
                className="w-full h-12 bg-sonas-accent text-sonas-bg font-bold rounded-2xl shadow-lg shadow-sonas-accent/20 gap-2"
              >
                {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={16} />}
                Deploy Broadcast
              </Button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Dispatch Logs</h3>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sonas-dim" />
              <Input 
                placeholder="Search history..." 
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10 h-10 bg-sonas-surface2 border-white/5 rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-4">
            {loading ? (
              <div className="py-20 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-sonas-dim" /></div>
            ) : filteredNotis.length === 0 ? (
              <div className="py-20 text-center text-sonas-dim text-xs font-bold uppercase tracking-widest">No history recorded</div>
            ) : filteredNotis.map(noti => (
              <div key={noti.id} className="bg-sonas-surface border border-sonas-border p-5 rounded-2xl group flex items-start justify-between gap-6 hover:border-sonas-accent/20 transition-all">
                <div className="flex gap-4">
                  <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shrink-0", 
                    noti.type === 'alert' ? "bg-red-500/10 text-red-500" : 
                    noti.type === 'warning' ? "bg-amber-500/10 text-amber-500" : 
                    noti.type === 'success' ? "bg-sonas-accent/10 text-sonas-accent" : "bg-blue-500/10 text-blue-500"
                  )}>
                    <Bell size={18} />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <h4 className="text-sm font-bold">{noti.title}</h4>
                      <span className={cn("text-[9px] font-bold px-2 py-0.5 rounded-full uppercase", 
                        noti.is_global ? "bg-sonas-accent/10 text-sonas-accent" : "bg-blue-500/10 text-blue-500"
                      )}>
                        {noti.is_global ? 'Platform' : 'Direct'}
                      </span>
                    </div>
                    <p className="text-xs text-sonas-muted leading-relaxed">{noti.message}</p>
                    <p className="text-[9px] text-sonas-dim font-mono">{new Date(noti.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleDelete(noti.id)}
                  className="p-2 rounded-xl text-sonas-dim hover:bg-red-500/10 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;